import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ContentChild, EventEmitter, HostListener,
  Inject,
  Input, NgZone,
  OnDestroy,
  OnInit, Optional, Output
} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {IntPortalSlotTemplateDirective} from "./int-portal-slot-template.directive";
import {IntPortalOutletService} from "../portal-outlet/int-portal-outlet.service";
import {DestroyStreamService, SatelliteService} from "../../services";
import {PayloadConfig as ISatellitePayload} from "satellite-droparea"
import {DOCUMENT} from "@angular/common";
import {INT_PORTAL_SLOT_UPDATE_POSITION_INTERVAL} from "./int-portal-slot-update-position-interval.token";

@Component({
  selector: 'int-portal-slot',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    DestroyStreamService,
    SatelliteService
  ]
})
export class IntPortalSlotComponent implements AfterViewInit, OnDestroy, OnInit {
  show$ = new BehaviorSubject(true);
  coords$ = new BehaviorSubject<{top: number, left: number; width: number} | null | undefined>(null)

  elementRef: HTMLElement | null = null;
  targetRef$ = new BehaviorSubject<HTMLElement | null | undefined>(null);

  @Input() satellitePayload: Partial<Omit<ISatellitePayload, 'target'|'dropper'|'viewport'>> = {}

  defaultConfig: Omit<ISatellitePayload, 'target'|'dropper'|'viewport'> = {
    positionSteps: ['bottom', 'top'],
    verticalOffset: 10,
    horizontalOffset: 0,
    viewportOffset: 10,
    customDropWidth: null,
    verticalAlign: "center",
    horizontalAlign: 'center',
  }

  @Input() payload: unknown;

  @Input() set show(isShow: boolean | null) {
    this.show$.next(Boolean(isShow));
  }

  @Input() set targetRef(target: HTMLElement | null | undefined) {
    if(this.targetRef$.value !== target) {
      this.targetRef$.next(target);
      this.changeDetector.detectChanges();
    }
  };

  @Input() viewportRef: HTMLElement | null = this.DocumentRef.documentElement;

  @ContentChild(IntPortalSlotTemplateDirective)
  private readonly portalDirective!: IntPortalSlotTemplateDirective;

  @HostListener('window:resize', ['$event'])
  resize() {
    this.updateCoords();
  }

  @HostListener('window:scroll', ['$event'])
  scroll() {
    this.updateCoords();
  }

  @Output() onDropSet = new EventEmitter<HTMLElement | null | undefined>();

  @Output() targetIsNotVisible = new EventEmitter();

  listenerStopId: any = 0;

  initSatelliteStopID: any = 0;

  constructor(
    private readonly portal: IntPortalOutletService,
    private readonly satelliteService: SatelliteService,
    @Inject(DOCUMENT)
    private readonly DocumentRef: Document,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly destroy$: DestroyStreamService,
    @Optional()
    @Inject(INT_PORTAL_SLOT_UPDATE_POSITION_INTERVAL) private readonly updatePositionInterval: number,
    private readonly zone: NgZone
  ) {

  }

  getSatelliteConfig(): Omit<ISatellitePayload, 'target'|'dropper'|'viewport'> {
    return {
      ...this.defaultConfig,
      ...this.satellitePayload
    }
  }

  initSatellite() {
    if(this.satelliteService) {
      this.satelliteService.destroy();
      clearTimeout(this.initSatelliteStopID);
    }

    if(this.targetRef$.value && this.viewportRef && this.elementRef) {
      this.satelliteService.create({
        ...this.getSatelliteConfig(),
        target: this.targetRef$.value,
        viewport: this.viewportRef,
        dropper: this.elementRef
      });

      this.zone.runOutsideAngular(() => {
        this.initSatelliteStopID = setTimeout(() => {
          this.updateCoords();
          this.changeDetector.markForCheck();
        }, 0);
      });

      if(this.satelliteService.instance) {
        this.satelliteService.instance.emit('target-out-of', () => {
          this.targetIsNotVisible.emit();
          this.changeDetector.markForCheck();
        });
      }
    }
  }

  updateCoords() {
    if(this.satelliteService.init) {
      this.coords$.next(this.satelliteService.instance?.getDropAreaOutputData())
    }
  }

  toggle(status: boolean): void {
    if (!this.portalDirective?.templateRef) {
      return undefined;
    }
    if (status) {
      this.showTemplate();
    } else {
      this.removeTemplate();
    }
  }

  showTemplate() {
    clearInterval(this.listenerStopId);
    const elements: Array<HTMLElement> | null = this.portal.setTemplateOut(
      this.portalDirective.templateRef,
      this.payload
    );
    if(elements) {
      this.elementRef = elements[0];
      this.onDropSet.emit(this.elementRef);

      this.initSatellite();

      this.zone.runOutsideAngular(() => {
        this.listenerStopId = setInterval(() => {
          this.updateCoords();
        }, this.updatePositionInterval);
      });
    }
  }

  removeTemplate() {
    this.portal.removeTemplateOut(this.portalDirective.templateRef);
    this.satelliteService.destroy();
    this.coords$.next(null);
    clearInterval(this.listenerStopId);
  }

  ngOnInit() {
    this.show$.pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        this.toggle(status);
      });

    this.targetRef$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.initSatellite();
      });

    this.coords$
      .pipe(takeUntil(this.destroy$))
      .subscribe((coords) => {
        if(coords && this.elementRef) {
          this.elementRef.style.width = `${coords.width}px`;
          this.elementRef.style.top = `${coords.top}px`;
          this.elementRef.style.left = `${coords.left}px`;
        }
      });
  }

  ngAfterViewInit() {
    if(!this.portalDirective?.templateRef) {
      console.log('Please provide [IntPortalSlotTemplateDirective]')
    }

    if (this.show$.value && this.portalDirective?.templateRef) {
      this.showTemplate();
    }
  }

  ngOnDestroy() {
    if (this.portalDirective?.templateRef) {
      this.removeTemplate();
    }

    this.show$.next(false);

    this.targetRef$.next(null);
    this.targetRef$.complete();

    this.coords$.next(null);
    this.coords$.complete();

    clearInterval(this.listenerStopId);
    clearTimeout(this.initSatelliteStopID);
  }
}
